import React, { useEffect, useState } from "react";
import { LayoutDashboard, Package, ShoppingCart, CalendarDays, BarChart3, LogOut, Lock, Loader2, Menu, Settings, Calculator } from "lucide-react";
import { adminApi, ADMIN_TOKEN_KEY } from "../../lib/api";
import { formatApiErrorDetail } from "../../lib/constants";
import AdminDashboard from "./sections/AdminDashboard";
import AdminProducts from "./sections/AdminProducts";
import AdminOrders from "./sections/AdminOrders";
import AdminMarketDays from "./sections/AdminMarketDays";
import AdminReports from "./sections/AdminReports";
import AdminMarketReview from "./sections/AdminMarketReview";
import AdminSettings from "./sections/AdminSettings";

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "products", label: "Products", icon: Package },
  { key: "orders", label: "Orders", icon: ShoppingCart },
  { key: "market", label: "Market Days", icon: CalendarDays },
  { key: "review", label: "Market Review", icon: Calculator },
  { key: "reports", label: "Reports", icon: BarChart3 },
  { key: "settings", label: "Settings", icon: Settings },
];

export default function AdminApp() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(ADMIN_TOKEN_KEY));
  const [section, setSection] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminName, setAdminName] = useState(localStorage.getItem("ayome_admin_username") || "");

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAuthed(false);
  };

  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#F3F4F1] font-sans flex" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-[#1A4331] text-white flex flex-col transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-6 border-b border-white/10">
          <h1 className="text-xl font-bold">Ayome Admin</h1>
          <p className="text-xs text-white/50 mt-1">Market operations</p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = section === n.key;
            return (
              <button
                key={n.key}
                data-testid={`admin-nav-${n.key}`}
                onClick={() => { setSection(n.key); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${active ? "bg-[#228B22] text-white" : "text-white/70 hover:bg-white/10"}`}
              >
                <Icon size={18} /> {n.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button data-testid="admin-logout" onClick={logout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 transition-colors duration-200">
            <LogOut size={18} /> Logout
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setSidebarOpen(true)}><Menu size={22} /></button>
            <h2 className="text-lg font-bold text-[#0A1F13] capitalize">{NAV.find((n) => n.key === section)?.label}</h2>
          </div>
          <div data-testid="admin-avatar" className="w-9 h-9 rounded-full bg-[#1A4331] text-white flex items-center justify-center text-sm font-bold">{(adminName || "A").charAt(0).toUpperCase()}</div>
        </header>
        <main className="flex-1 p-4 lg:p-8 overflow-x-auto">
          {section === "dashboard" && <AdminDashboard />}
          {section === "products" && <AdminProducts />}
          {section === "orders" && <AdminOrders />}
          {section === "market" && <AdminMarketDays />}
          {section === "review" && <AdminMarketReview />}
          {section === "reports" && <AdminReports />}
          {section === "settings" && <AdminSettings onUsernameChange={setAdminName} />}
        </main>
      </div>
    </div>
  );
}

function AdminLogin({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await adminApi.post("/admin/login", { username, password });
      localStorage.setItem(ADMIN_TOKEN_KEY, data.access_token);
      localStorage.setItem("ayome_admin_username", data.admin?.username || "");
      onSuccess();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A4331] flex items-center justify-center px-6" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="w-full max-w-sm bg-white rounded-2xl p-8 shadow-xl">
        <div className="w-12 h-12 rounded-xl bg-[#1A4331] flex items-center justify-center text-white mb-5"><Lock size={22} /></div>
        <h1 className="text-2xl font-bold text-[#0A1F13]">Admin Login</h1>
        <p className="text-gray-500 text-sm mt-1 mb-6">Sign in to manage Ayome Groceries.</p>
        <form onSubmit={submit} className="space-y-3">
          {error && <div data-testid="admin-login-error" className="rounded-lg bg-[#FFF0F0] border border-[#FFD0D0] px-3 py-2.5 text-sm text-[#991B1B]">{error}</div>}
          <input data-testid="admin-username-input" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full h-12 px-4 rounded-lg bg-gray-100 focus:bg-white border border-transparent focus:border-[#228B22] outline-none transition-colors" />
          <input data-testid="admin-password-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full h-12 px-4 rounded-lg bg-gray-100 focus:bg-white border border-transparent focus:border-[#228B22] outline-none transition-colors" />
          <button data-testid="admin-login-button" type="submit" disabled={loading} className="w-full h-12 rounded-lg bg-[#228B22] hover:bg-[#1A4331] text-white font-semibold flex items-center justify-center transition-colors duration-200 disabled:opacity-60">
            {loading ? <Loader2 size={20} className="animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
